import { supabase } from '../lib/supabase';
import { getPlanConfig, normalizePlanSlug, PAID_PLAN_SLUGS, PLAN_SLUGS } from '../config/plans';

export const stripeService = {
  /**
   * Create Stripe checkout session for subscription
   */
  async createCheckoutSession(
    planSlug,
    userId,
    customerEmail,
    billingPeriod = 'month'
  ) {
    try {
      const normalizedPlanSlug = normalizePlanSlug(planSlug);
      if (!PAID_PLAN_SLUGS.includes(normalizedPlanSlug)) {
        throw new Error('Plan invalide pour Stripe Checkout');
      }

      const { data, error } = await supabase?.functions?.invoke('create-checkout-session', {
        body: {
          planSlug: normalizedPlanSlug,
          userId,
          customerEmail,
          billingPeriod,
          successUrl: `${window.location?.origin}/main-dashboard?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location?.origin}/subscription-pricing?checkout=cancelled`,
        },
      });

      if (error) {
        console.error('Checkout session error:', error);
        const errorMessage = error?.message || data?.error || 'Failed to create checkout session';
        throw new Error(errorMessage);
      }

      if (!data?.url) throw new Error('No checkout URL returned from server');

      return { data, error: null };
    } catch (err) {
      console.error('Checkout session creation error:', err);
      return { data: null, error: err?.message || 'Failed to create checkout session' };
    }
  },

  async startCheckout(planSlug, userId, customerEmail) {
    return this.createCheckoutSession(planSlug, userId, customerEmail, 'month');
  },

  async assignTrialPlan(userId) {
    if (!userId) return { data: null, error: 'Aucun utilisateur connecté' };
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update({ subscription_tier: PLAN_SLUGS.TRIAL })
        ?.eq('id', userId)
        ?.select()
        ?.maybeSingle();
      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      return { data: null, error: err?.message || "Impossible d'activer l'essai gratuit" };
    }
  },

  /**
   * Create Stripe customer portal session
   */
  async createPortalSession(customerId) {
    try {
      const { data, error } = await supabase?.functions?.invoke('create-portal-session', {
        body: {
          customerId,
          returnUrl: `${window.location?.origin}/main-dashboard`,
        },
      });

      if (error) {
        console.error('Portal session error:', error);
        const errorMessage = error?.message || data?.error || 'Failed to create portal session';
        throw new Error(errorMessage);
      }

      if (!data?.url) throw new Error('No portal URL returned from server');

      return { data, error: null };
    } catch (err) {
      console.error('Portal session creation error:', err);
      return { data: null, error: err?.message || 'Failed to create portal session' };
    }
  },

  /**
   * Get user's active subscription
   */
  async getUserSubscription(userId) {
    try {
      const { data, error } = await supabase
        ?.from('subscriptions')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.in('status', ['active', 'trialing'])
        ?.order('created_at', { ascending: false })
        ?.limit(1)
        ?.single();

      // PGRST116 = No rows (ok)
      if (error && error?.code !== 'PGRST116') {
        console.error('Get subscription error:', error);
        throw error;
      }

      return { data, error: null };
    } catch (err) {
      console.error('Get subscription error:', err);
      return { data: null, error: err };
    }
  },

  /**
   * Check if user has active subscription
   */
  async hasActiveSubscription(userId) {
    try {
      const { data } = await this.getUserSubscription(userId);
      return !!data && ['active', 'trialing'].includes(data?.status);
    } catch (err) {
      console.error('Check active subscription error:', err);
      return false;
    }
  },

  /**
   * Get user's payment history
   */
  async getPaymentHistory(userId) {
    try {
      const { data, error } = await supabase
        ?.from('payment_transactions')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Get payment history error:', err);
      return { data: null, error: err };
    }
  },

  /**
   * Get Stripe product configurations (if you use this table)
   */
  async getStripeProducts() {
    try {
      const { data, error } = await supabase
        ?.from('stripe_products')
        ?.select('*')
        ?.eq('is_active', true)
        ?.order('price', { ascending: true });

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Get Stripe products error:', err);
      return { data: null, error: err };
    }
  },

  /**
   * Format amount for display
   */
  formatAmount(amount, currency = 'CAD') {
    return new Intl.NumberFormat('fr-CA', {
      style: 'currency',
      currency,
    }).format(amount);
  },

  /**
   * Normalize tier (compat legacy)
   */
  normalizeTier(tier) {
    return normalizePlanSlug(tier);
  },

  /**
   * Get tier display name
   */
  getTierDisplayName(tier) {
    const t = this.normalizeTier(tier);
    return getPlanConfig(t).name;
  },

  /**
   * Get tier color
   */
  getTierColor(tier) {
    const t = this.normalizeTier(tier);
    const colors = {
      trial: 'var(--color-muted-foreground)',
      analysis: 'var(--color-success)',
      pro: 'var(--color-primary)',
    };
    return colors[t] || 'var(--color-muted-foreground)';
  },

  /**
   * Get tier limits
   */
  getTierLimits(tier) {
    const limits = {
      trial: {
        properties: 3,
        propertiesPerMonth: 3,
        scenarios: 0,
        scenariosPerProperty: 0,
        projectionYears: 30,
        portfolioManagement: true,
        planning: false,
        pdfReports: true,
        aiImport: true,
        aiImportsPerMonth: 3,
        aiAnalysesPerMonth: 3,
        aiAnalysis: true,
        stressTests: false,
        multiScenarioCompare: false,
      },

      analysis: {
        properties: Infinity,
        propertiesPerMonth: Infinity,
        scenarios: Infinity,
        scenariosPerProperty: Infinity,
        projectionYears: 30,
        portfolioManagement: true,
        planning: false,
        pdfReports: true,
        aiImport: true,
        aiImportsPerMonth: Infinity,
        aiAnalysesPerMonth: Infinity,
        aiAnalysis: true,
        stressTests: true,
        multiScenarioCompare: false,
      },

      pro: {
        properties: Infinity,
        propertiesPerMonth: Infinity,
        scenarios: Infinity,
        scenariosPerProperty: Infinity,
        projectionYears: 30,
        portfolioManagement: true,
        planning: true,
        pdfReports: true,
        aiImport: true,
        aiImportsPerMonth: Infinity,
        aiAnalysesPerMonth: Infinity,
        aiAnalysis: true,
        stressTests: true,
        multiScenarioCompare: true,
      },
    };

    const t = this.normalizeTier(tier);
    return limits[t] || limits.trial;
  },

  /**
   * Cancel user subscription
   * NOTE: verify your function name casing: 'cancel-subscription' vs 'Cancel-subscription'
   */
  async cancelSubscription(userId, cancellationData) {
    try {
      const { data, error } = await supabase?.functions?.invoke('cancel-subscription', {
        body: {
          userId,
          reason: cancellationData?.reason,
          reasonLabel: cancellationData?.reasonLabel,
          feedback: cancellationData?.feedback,
        },
      });

      if (error) {
        console.error('Cancel subscription error:', error);
        const errorMessage = error?.message || data?.error || 'Failed to cancel subscription';
        throw new Error(errorMessage);
      }

      return { data, error: null };
    } catch (err) {
      console.error('Cancel subscription error:', err);
      return { data: null, error: err?.message || 'Failed to cancel subscription' };
    }
  },

  /**
   * Change subscription plan
   */
  async changeSubscriptionPlan(newTier, billingPeriod = 'month', hasFirstYearDiscount = false) {
    try {
      const { data, error } = await supabase?.functions?.invoke('change-subscription-plan', {
        body: {
          newTier,
          billingPeriod,
          hasFirstYearDiscount,
        },
      });

      if (error) {
        console.error('Change subscription plan error:', error);
        const errorMessage = error?.message || data?.error || 'Failed to change subscription plan';
        throw new Error(errorMessage);
      }

      return { data, error: null };
    } catch (err) {
      console.error('Change subscription plan error:', err);
      return { data: null, error: err?.message || 'Failed to change subscription plan' };
    }
  },
};
